import type { FolderUploadJobState, FolderUploadsSnapshot } from './types';

/** Byte progress re-renders the panel at most this often. */
export const FOLDER_UPLOAD_PROGRESS_NOTIFY_MS = 200;

export type FolderUploadStore = ReturnType<typeof createFolderUploadStore>;

/**
 * The app-wide list of folder uploads, read through `useSyncExternalStore`.
 * It outlives the page that started an upload, so in-app navigation keeps it.
 * Status changes notify at once; byte progress is coalesced.
 */
export function createFolderUploadStore(
  options: { progressNotifyMs?: number } = {}
) {
  const progressNotifyMs =
    options.progressNotifyMs ?? FOLDER_UPLOAD_PROGRESS_NOTIFY_MS;
  let snapshot: FolderUploadsSnapshot = { jobs: [] };
  const listeners = new Set<() => void>();
  let pendingNotify: ReturnType<typeof setTimeout> | null = null;
  const notify = () => {
    if (pendingNotify !== null) {
      clearTimeout(pendingNotify);
      pendingNotify = null;
    }
    for (const listener of listeners) listener();
  };
  const notifySoon = () => {
    if (pendingNotify !== null) return;
    pendingNotify = setTimeout(notify, progressNotifyMs);
  };
  const setJobs = (
    jobs: readonly FolderUploadJobState[],
    coalesce: boolean
  ) => {
    snapshot = { jobs };
    if (coalesce) notifySoon();
    else notify();
  };
  return {
    getSnapshot: (): FolderUploadsSnapshot => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    get: (jobId: string) => snapshot.jobs.find((job) => job.id === jobId),
    /** Newest first, so the panel shows the latest upload on top. */
    add(job: FolderUploadJobState) {
      setJobs(
        [job, ...snapshot.jobs.filter((item) => item.id !== job.id)],
        false
      );
    },
    update(
      jobId: string,
      recipe: (job: FolderUploadJobState) => FolderUploadJobState,
      options?: { coalesce?: boolean }
    ) {
      let changed = false;
      const jobs = snapshot.jobs.map((job) => {
        if (job.id !== jobId) return job;
        const next = recipe(job);
        if (next !== job) changed = true;
        return next;
      });
      if (changed) setJobs(jobs, options?.coalesce === true);
    },
    remove(jobId: string) {
      if (!snapshot.jobs.some((job) => job.id === jobId)) return;
      setJobs(
        snapshot.jobs.filter((job) => job.id !== jobId),
        false
      );
    },
  };
}
